import { useEffect, useState } from 'react'

import { fetchBlobUrl } from '../lib/api'
import { useSession } from '../lib/SessionContext'

/* ============================================================================
   สไลด์ banner หน้าแรก (Home.tsx) — ฝั่งพนักงาน
   API: GET /api/slides/feed · รูปที่ /api/slides/:id/image (backend/routes/adminSlides.js)

   รูปโหลดผ่าน fetch (ส่ง cookie) แล้วทำเป็น blob URL — ไม่ใช้ <img src> ข้าม origin ตรง ๆ
   เพราะ <img> ข้าม origin ไม่ส่ง cookie session (third-party cookie ถูกบล็อก) → รูป 401 แตก
   (เป็นกับดักเดียวกับไฟล์ข่าว ดู api.openFile/fetchBlobUrl) · revoke blob ตอน unmount

   เลื่อนเองทุก 5 วิ · กดจุดเลือกได้ · มีสไลด์เดียวไม่ต้องมีปุ่ม · ไม่มีสไลด์ = เงียบ
   ============================================================================ */

type Slide = {
  id: number
  title: string | null
  linkUrl: string | null
}

const INTERVAL_MS = 5000

/**
 * รูปสไลด์หนึ่งใบ — โหลดเองผ่าน fetch (ส่ง cookie) แล้วทำเป็น blob
 *
 * ⚠️ แยกเป็น component ต่อรูป (ไม่ batch เก็บ URL ไว้ที่ parent) เพราะถ้า parent revoke
 *    ตอน re-render/remount จะชน: blob ถูกปล่อยก่อน <img> วาด → รูปหาย (เคยพลาดแบบนี้)
 *    ตัวนี้ revoke เฉพาะ URL ของตัวเองตอน unmount จริง ไม่ยุ่งกับใบอื่น
 */
function SlideImg({ id, title }: { id: number; title: string | null }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    let made: string | null = null
    fetchBlobUrl(`/api/slides/${id}/image`)
      .then((u) => {
        if (!alive) {
          URL.revokeObjectURL(u)
          return
        }
        made = u
        setUrl(u)
      })
      .catch(() => {})
    return () => {
      alive = false
      if (made) URL.revokeObjectURL(made)
    }
  }, [id])
  // ยังโหลดไม่เสร็จ — กันพื้นที่ไว้ (ratio ใกล้ banner) ไม่ให้หน้ากระโดด
  if (!url) return <div className="slide-img slide-img--loading" aria-hidden="true" />
  return <img className="slide-img" src={url} alt={title ?? 'สไลด์'} />
}

export default function SlideShow() {
  const { call } = useSession()
  const [slides, setSlides] = useState<Slide[] | null>(null)
  const [idx, setIdx] = useState(0)

  useEffect(() => {
    let alive = true
    call<{ items: Slide[] }>('/api/slides/feed')
      .then((d) => alive && setSlides(d.items))
      .catch(() => alive && setSlides([])) // เงียบ — หน้าแรกไม่ควรมีกล่อง error ของ banner
    return () => {
      alive = false
    }
  }, [call])

  // เลื่อนอัตโนมัติ — รีเซ็ตทุกครั้งที่ idx เปลี่ยน (กดเองแล้วไม่กระตุกเพราะตัวตั้งเวลาเก่า)
  useEffect(() => {
    if (!slides || slides.length <= 1) return
    const t = setTimeout(() => setIdx((i) => (i + 1) % slides.length), INTERVAL_MS)
    return () => clearTimeout(t)
  }, [slides, idx])

  if (!slides || slides.length === 0) return null

  const cur = slides[Math.min(idx, slides.length - 1)]

  return (
    <section className="slideshow" aria-label="สไลด์ประชาสัมพันธ์">
      <div className="slide-stage">
        {cur.linkUrl ? (
          <a href={cur.linkUrl} target="_blank" rel="noopener noreferrer">
            <SlideImg id={cur.id} title={cur.title} />
          </a>
        ) : (
          <SlideImg id={cur.id} title={cur.title} />
        )}
        {cur.title && <div className="slide-caption">{cur.title}</div>}
      </div>

      {slides.length > 1 && (
        <div className="slide-dots">
          {slides.map((s, i) => (
            <button
              key={s.id}
              type="button"
              className={i === idx ? 'slide-dot slide-dot--on' : 'slide-dot'}
              aria-label={`ไปสไลด์ที่ ${i + 1}`}
              aria-current={i === idx}
              onClick={() => setIdx(i)}
            />
          ))}
        </div>
      )}
    </section>
  )
}
